import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { Button, CircularProgress } from "@mui/material";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Unstable_Grid2";
import axios from "axios";
import { api_base_url } from "config";
import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Limpa mensagem de erro quando usuário começa a digitar novamente
  useEffect(() => {
    if (errorMessage) setErrorMessage("");
  }, [username, password]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    if (!username.trim()) {
      setErrorMessage("Nome de usuário é obrigatório");
      return false;
    }
    if (!password.trim()) {
      setErrorMessage("Senha é obrigatória");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    // Implementa delay crescente após tentativas falhas
    const delayTime = Math.min(attempts * 1000, 5000);
    if (delayTime > 0) {
      await new Promise(resolve => setTimeout(resolve, delayTime));
    }

    const user = {
      username: username.trim(),
      password: password,
    };

    const url = api_base_url + "/dashboard/token/";

    try {
      const { data } = await axios.post(
        url,
        user,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
        { withCredentials: true },
      );

      localStorage.clear();
      localStorage.setItem("access_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${data["access"]}`;
      window.location.href = "/";
    } catch (error) {
      console.error(error);
      setAttempts(prev => prev + 1);
      
      // Mensagens de erro mais informativas (sem revelar detalhes de segurança)
      if (error.response) {
        if (error.response.status === 401) {
          setErrorMessage("Nome de usuário ou senha incorretos");
        } else if (error.response.status === 429) {
          setErrorMessage("Muitas tentativas. Tente novamente mais tarde.");
        } else {
          setErrorMessage("Erro ao autenticar. Tente novamente.");
        }
      } else if (error.request) {
        setErrorMessage("Não foi possível conectar ao servidor. Verifique sua conexão.");
      } else {
        setErrorMessage("Erro ao fazer login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid
      container
      height="100vh"
      justifyContent="center"
      alignItems="center"
      sx={{ backgroundColor: "snoopy.orange.main" }}
    >
      <Grid xs={12} sm={10} md={8} lg={6}>
        <Card 
          sx={{ 
            maxHeight: "90vh",
            overflow: "auto",
            boxShadow: 8,
            borderRadius: 2
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h4" component="h1" sx={{ textAlign: "center", mb: 2 }}>
            </Typography>
            
            <CardMedia
              component="img"
              image="/snoopy-welcome.gif"
              alt="Snoopy dando boas vindas"
              sx={{
                maxHeight: "30vh",
                width: "auto",
                marginLeft: "auto",
                marginRight: "auto",
                objectFit: "contain"
              }}
            />
            
            <CardContent>
              <Box 
                component="form" 
                onSubmit={submit} 
                sx={{ 
                  textAlign: "center",
                  maxWidth: "400px",
                  margin: "0 auto"
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Nome de usuário"
                  name="username"
                  type="text"
                  value={username}
                  required
                  onChange={(e) => setUsername(e.target.value)}
                  sx={{ mb: 2 }}
                  autoComplete="username"
                  autoFocus
                  inputProps={{ 
                    maxLength: 100,
                    "aria-label": "Nome de usuário"
                  }}
                />
                
                <TextField
                  fullWidth
                  variant="outlined"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  label="Senha"
                  value={password}
                  required
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 3 }}
                  autoComplete="current-password"
                  inputProps={{ 
                    "aria-label": "Senha"
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="alternar visibilidade da senha"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  endIcon={isLoading ? null : <FlightTakeoffIcon />}
                  sx={{ 
                    backgroundColor: "snoopy.yellow.main",
                    color: "text.primary",
                    fontWeight: "bold",
                    '&:hover': {
                      backgroundColor: "snoopy.yellow.dark",
                    },
                    mb: 2,
                    minWidth: "150px"
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Entrar"
                  )}
                </Button>
                
                {errorMessage && (
                  <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                  >
                    {errorMessage}
                  </Alert>
                )}
                
                <Typography variant="body2" sx={{ mt: 3, color: "text.secondary" }}>
                  Esqueceu sua senha? Entre em contato com o administrador. 
                </Typography>
              </Box>
            </CardContent>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};